import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useBuildStore } from '@/store/build-store'
import { useConfigStore } from '@/store/config-store'
import { BuilderLayout } from '@/components/layout/BuilderLayout'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { submitLead, uploadSnapshot } from '@/lib/api'
import { trackFormStarted, trackLeadSubmitted } from '@/lib/tracking'

const TIMELINES = [
  { value: 'immediately', label: 'Immediately' },
  { value: '1-3 months', label: '1–3 months' },
  { value: '3-6 months', label: '3–6 months' },
  { value: '6-12 months', label: '6–12 months' },
  { value: 'just-exploring', label: 'Just exploring' },
]

const BUDGETS = [
  { value: 'under-75k', label: 'Under $75k' },
  { value: '75k-100k', label: '$75k–$100k' },
  { value: '100k-150k', label: '$100k–$150k' },
  { value: '150k-plus', label: '$150k+' },
  { value: 'not-sure', label: 'Not sure yet' },
]

const CONTACT_METHODS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone call' },
  { value: 'text', label: 'Text message' },
]

export default function LeadForm() {
  const navigate = useNavigate()
  const { selectedModelId, selectedOptionIds, customerDraft, setCustomerField, setSubmitted, snapshotDataUrl } = useBuildStore()
  const { config } = useConfigStore()
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState('')

  useEffect(() => {
    if (selectedModelId) trackFormStarted(selectedModelId)
  }, [selectedModelId])

  if (!config || !selectedModelId) {
    navigate('/build/model')
    return null
  }

  const validate = () => {
    const e: Record<string, string> = {}
    if (!customerDraft.name.trim()) e.name = 'Name is required.'
    if (!customerDraft.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerDraft.email))
      e.email = 'A valid email address is required.'
    if (!customerDraft.phone.trim() || customerDraft.phone.replace(/\D/g, '').length < 10)
      e.phone = 'A valid phone number is required.'
    return e
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const fieldErrors = validate()
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors)
      return
    }

    setSubmitting(true)
    setServerError('')
    try {
      // Upload the 3D snapshot captured on the Review page (non-blocking on failure)
      let snapshotUrl: string | undefined
      if (snapshotDataUrl) {
        try {
          const [header, data] = snapshotDataUrl.split(',')
          const mime = header.match(/data:([^;]+)/)?.[1] ?? 'image/jpeg'
          const bytes = atob(data)
          const arr = new Uint8Array(bytes.length)
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i)
          const blob = new Blob([arr], { type: mime })
          const uploaded = await uploadSnapshot(blob)
          snapshotUrl = uploaded.url
        } catch { /* non-blocking — proceed without snapshot */ }
      }

      const result = await submitLead({
        model_id: selectedModelId,
        option_ids: selectedOptionIds,
        snapshot_url: snapshotUrl,
        customer: {
          name: customerDraft.name,
          email: customerDraft.email,
          phone: customerDraft.phone,
          location: customerDraft.location || undefined,
          timeline: customerDraft.timeline || undefined,
          budget_range: customerDraft.budget_range || undefined,
          financing_interest: customerDraft.financing_interest,
          contact_method: customerDraft.contact_method || undefined,
          notes: customerDraft.notes || undefined,
        },
      })
      setSubmitted(result.lead_id, result.estimated_total)
      trackLeadSubmitted(selectedModelId, result.lead_id, result.estimated_total)
      navigate('/build/thanks')
    } catch (err) {
      setServerError((err as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <BuilderLayout showSummary={false}>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Request a Quote</h1>
        <p className="text-slate-500">Tell us a bit about yourself and we'll follow up with next steps.</p>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className="bg-white rounded-xl border border-slate-200 shadow-card p-6 mb-5 space-y-5">
          <h2 className="font-semibold text-slate-800">Contact Information</h2>

          <Input
            label="Full Name"
            required
            value={customerDraft.name}
            onChange={(e) => setCustomerField('name', e.target.value)}
            error={errors.name}
            placeholder="Jane Smith"
          />
          <Input
            label="Email Address"
            type="email"
            required
            value={customerDraft.email}
            onChange={(e) => setCustomerField('email', e.target.value)}
            error={errors.email}
            placeholder="jane@example.com"
          />
          <Input
            label="Phone Number"
            type="tel"
            required
            value={customerDraft.phone}
            onChange={(e) => setCustomerField('phone', e.target.value)}
            error={errors.phone}
            placeholder="(541) 555-0100"
          />
          <Input
            label="Location / State"
            value={customerDraft.location}
            onChange={(e) => setCustomerField('location', e.target.value)}
            placeholder="Oregon, WA, etc."
          />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-card p-6 mb-5 space-y-5">
          <h2 className="font-semibold text-slate-800">Build Details</h2>

          <Select
            label="When are you looking to build?"
            options={TIMELINES}
            placeholder="Select a timeline"
            value={customerDraft.timeline}
            onChange={(e) => setCustomerField('timeline', e.target.value)}
          />
          <Select
            label="Budget range"
            options={BUDGETS}
            placeholder="Select a range"
            value={customerDraft.budget_range}
            onChange={(e) => setCustomerField('budget_range', e.target.value)}
          />
          <Select
            label="Preferred contact method"
            options={CONTACT_METHODS}
            placeholder="Select one"
            value={customerDraft.contact_method}
            onChange={(e) => setCustomerField('contact_method', e.target.value)}
          />

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-slate-700">Additional Notes</label>
            <textarea
              rows={3}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors resize-none"
              placeholder="Site details, questions, land status, etc."
              value={customerDraft.notes}
              onChange={(e) => setCustomerField('notes', e.target.value)}
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 rounded border-slate-300 text-brand-500 focus:ring-brand-500"
              checked={customerDraft.financing_interest}
              onChange={(e) => setCustomerField('financing_interest', e.target.checked)}
            />
            <span className="text-sm text-slate-600">I'm interested in financing options</span>
          </label>
        </div>

        {config.settings['pricing_disclaimer'] && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
            <p className="text-xs text-slate-500 leading-relaxed">
              {config.settings['pricing_disclaimer']}
            </p>
          </div>
        )}

        {serverError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5 text-sm text-red-700">
            {serverError}
          </div>
        )}

        <div className="flex items-center justify-between">
          <Button variant="secondary" type="button" onClick={() => navigate('/build/review')}>
            <ChevronLeft className="h-4 w-4" /> Back
          </Button>
          <Button type="submit" size="lg" loading={submitting}>
            Submit Build →
          </Button>
        </div>
      </form>
    </BuilderLayout>
  )
}
